import React, {useState} from 'react'
import PropTypes from "prop-types";
import { OverlayTrigger, Popover } from "react-bootstrap";
import EntityPopover from "./EntityPopover";
import OutboundLink from "./OutboundLink";

import {fetchWikiImage, NOT_FOUND, PENDING} from "../lib/Wikipedia";
import {USE_LOGOS} from "./Logo";
import MoleculeImage from "./MoleculeImage";
import ProteinImage from "./ProteinImage";
import {createSourceLink} from "../lib/links";
import {Ball} from "./Ball";

/**
 * Check if entity has protein identifiers (UniProt or PDB IDs)
 */
const hasProteinIdentifiers = (entity) => {
  if (!entity) return false;

  // Check accession field
  if (entity.accession) return true;

  // Check aliases for UniProt or PDB patterns
  if (entity.aliases && Array.isArray(entity.aliases)) {
    // UniProt pattern: P12345, Q9Y6K9, etc.
    const uniprotPattern = /^[OPQ][0-9][A-Z0-9]{3}[0-9]$|^[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}$/;
    // PDB pattern: 1ABC, 2XYZ (4 characters: 1 digit + 3 alphanumeric)
    const pdbPattern = /^[0-9][A-Z0-9]{3}$/i;

    return entity.aliases.some(alias =>
      typeof alias === 'string' &&
      (uniprotPattern.test(alias.toUpperCase()) ||
       (alias.length === 4 && pdbPattern.test(alias)))
    );
  }

  return false;
};

/**
 * Truncates a label if it would span more than ~2 lines (approximately 60 characters)
 */
const truncateLabel = (label, maxLength = 60) => {
  if (!label || label.length <= maxLength) {
    return label;
  }
  // Find the middle point
  const halfLength = Math.floor(maxLength / 2) - 3; // Reserve 6 chars for "..."
  const start = label.substring(0, halfLength);
  const end = label.substring(label.length - halfLength);
  return `${start}...${end}`;
};

const EntityView = ({
    title = null,
    scale = "lg",
    text = null,
    wikiTitle = null,
    entity = null,
    similarNeighbor = null,
    encoding = null, // deprecated => entity.smiles | entity.inchi
    fallback = null, // deprecated => text
    terms = null, // deprecated => wikiTitle
    disableRotation = false,
    // Label-related props
    showLabel = false,
    markText = null,
    truncate = false,
    className = null,
    showCategory = null,
    dataset = null,
    datasets = null,
    disableSourceLink = false,
    actions = {},
    edgeIndex = null,
    entityImageRef = null,
    labelOrientation = "column", // "column" (label above/below image) or "row" (label beside image)
    enableOverlay = true, // Enable click-to-show overlay with entity details
  }) => {
  const [url, setURL] = useState(null);
  const imageRef = entityImageRef || React.createRef();
  const [showOverlay, setShowOverlay] = useState(false);

  if (!title && entity) {
    title = entity.label;
  }
  if (!text && entity) {
    text = entity.categoryName != null ? String(entity.categoryName) : entity.categoryName;
  }
  if (fallback != null) {
    text = fallback;
  }
  if (terms != null) {
    wikiTitle = terms;
  }

  // Determine if category should be shown (default logic from LabeledEntity)
  const defaultShowCategory = entity && (entity.encoding || entity.smiles || entity.inchi);
  const shouldShowCategory = showCategory !== null ? showCategory : defaultShowCategory;

  const ballText = text && typeof text === 'string' && (text.split(',').length > 1 ? text.split(',')[0] : text);
  if (url === null && wikiTitle && text === 'org' && USE_LOGOS) {
    fetchWikiImage(wikiTitle).then((url) => {
      setURL(url);
    });
  }

  // Render the entity image
  let entityImage = null;

  // Check for protein entities first - only show protein structures for target category
  if (entity && entity.category === 'target' && hasProteinIdentifiers(entity)) {
    entityImage = (
      <ProteinImage
        entity={entity}
        className="entity"
        title={entity.categoryName}
        scale={scale}
        disableRotation={disableRotation}
      />
    );
  }
  // Check for molecule entities
  else if (encoding || (entity && (entity.smiles || entity.inchi))) {
    entityImage = (
      <MoleculeImage
        encoding={encoding || (entity && (entity.smiles || entity.inchi))}
        className="entity"
        title={entity ? entity.categoryName : undefined}
        scale={scale}
        similarNeighbor={similarNeighbor}
        disableRotation={disableRotation}
      />
    );
  }
  else {
    entityImage = !url || url === NOT_FOUND || url === PENDING
      ? (<Ball className="entity category" title={title} text={ballText} scale={scale} />)
      : (<img src={url} alt={title}
              className={`entity ball ball-${scale} logo-image`}
              onError={e => setURL(NOT_FOUND)}
         />);
  }

  // If no label is requested, return just the image
  if (!showLabel || !entity) {
    return entityImage;
  }

  // Handle labeled entity
  const {category, categoryName} = entity;

  // Only create source link if not disabled and we have dataset metadata available
  // Skip calling createSourceLink if datasets is empty to avoid console errors during initial load
  const datasetsToUse = datasets || (dataset ? {[dataset.id]: dataset} : {});
  const hasDatasets = Object.keys(datasetsToUse).length > 0;
  const srcLink = (disableSourceLink || !hasDatasets) ? null : createSourceLink(entity, datasetsToUse);

  // Apply truncation if requested
  const displayLabel = truncate ? truncateLabel(entity.label) : entity.label;
  const displayPreferredName = truncate && entity.preferredName ? truncateLabel(entity.preferredName) : entity.preferredName;

  const handleCompare = () => {
    setShowOverlay(false);
    if (actions.onComparisonClick && edgeIndex !== null) {
      actions.onComparisonClick(edgeIndex);
    }
  };

  // Determine label class based on category
  const labelClass = category === 'publication' ? 'publication' : category === 'patent' ? 'patent' : '';

  const orientationClass = `entity-view-${labelOrientation}`;

  // Image wrapper - conditionally wrapped with overlay trigger
  const imageWrapper = (
    <span>
      <div className="ev-ch-fgm-node-img" ref={imageRef}>
        {entityImage}
      </div>
    </span>
  );

  const imageWithOptionalOverlay = enableOverlay ? (
    <OverlayTrigger
      defaultShow={false}
      delay={0.2}
      flip={false}
      popperConfig={{}}
      target={null}
      trigger={"click"}
      rootClose={true}
      show={showOverlay}
      onToggle={()=>setShowOverlay(!showOverlay)}
      onHide={()=>setShowOverlay(false)}
      placement="bottom"
      overlay={
        <Popover id="popup-node" className="ev-ch-popup" onClick={()=>setShowOverlay(false)}>
          <EntityPopover
            entity={entity}
            similarNeighbor={similarNeighbor}
            onCompare={similarNeighbor ? handleCompare : null}
          />
        </Popover>
      }
    >
      {imageWrapper}
    </OverlayTrigger>
  ) : imageWrapper;

  return (
    <div className={`entity-view ev-ch-fgm-wrp ev-ch-resp-body-${scale} ${orientationClass}${className ? ' ' + className : ''}`}>
      {imageWithOptionalOverlay}
      <h4>
        {srcLink
         ? (<OutboundLink
            className={`ev-ch-fgm-node-label${labelClass ? ' ' + labelClass : ''}`}
            title={truncate ? (entity.preferredName || entity.label) : (/^(query|smiles)/i.test(srcLink.title) ? "" : `Click to view this entity within the ${srcLink.title} website\n\n${srcLink.description}`)}
            href={srcLink.url}
          >
            {markText ? markText(displayLabel) : (displayPreferredName || displayLabel)}
          </OutboundLink>)
         : (<span className={`ev-ch-fgm-node-label${labelClass ? ' ' + labelClass : ''}`}>
            {markText ? markText(displayLabel) : (displayPreferredName || displayLabel || title)}
          </span>)}
      </h4>
      {shouldShowCategory && (
        <h5 className="ev-ch-fgm-node-type category" title={category}>{categoryName}</h5>
      )}
    </div>
  );
};

export default EntityView;
